import React, { useState, useRef, useEffect } from 'react'
import CvMultiStepProgress from './CvMultiStepProgress';
import { Form, Button, Card, Container, Row, Col } from 'react-bootstrap'
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getUserDetails, updateUserProfile } from '../../../actions/userActions';
import { USER_UPDATE_PROFILE_RESET } from '../../../constants/userConstants';
import { motion } from 'framer-motion';
import { FaArrowLeft, FaArrowRight, FaCamera, FaTrash } from 'react-icons/fa';
import GradientButton from '../../../components/GradientButton';

function CvGeneratorPersonalInfo() {
  const navigate = useNavigate();
  const [photoPreview, setPhotoPreview] = useState(null);

  const photoInputMobileRef = useRef(null);
  const photoInputDesktopRef = useRef(null);

  // Pola z UserProfile
  const [name, setName] = useState('');
  const [lastName, setLastName] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [, setPhotoUrl] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);

  const dispatch = useDispatch();

  const userDetails = useSelector(state => state.userDetails);
  const { user } = userDetails;

  const userLogin = useSelector(state => state.userLogin);
  const { userInfo } = userLogin;

  const userUpdateProfile = useSelector(state => state.userUpdateProfile);
  const { success: updateSuccess } = userUpdateProfile;

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

  // Photo URL builder - backend returns /staticfiles/media/...
  const buildPhotoUrl = (photoPath) => {
    if (!photoPath) return null;
    
    if (photoPath.startsWith('http') || photoPath.startsWith('data:')) {
      return photoPath;
    }
    
    // Always prepend BACKEND_URL to relative paths from backend
    // This ensures frontend (port 3000) can access backend (port 5000) staticfiles
    const serverBase = BACKEND_URL.replace(/\/api\/?$/, '');
    const photoUrl = `${serverBase}${photoPath}?t=${new Date().getTime()}`;
    
    return photoUrl;
  };

  useEffect(() => {
    if (!userInfo) {
      navigate('/login');
      return;
    }
    
    if (!user || !user.name || updateSuccess) {
      dispatch({ type: USER_UPDATE_PROFILE_RESET });
      dispatch(getUserDetails('profile'));
    } else {
      setName(user.name || '');
      setLastName(user.last_name || '');
      setBirthDate(user.birth_date ? user.birth_date.substring(0, 10) : '');
      setAddress(user.address || '');
      setCity(user.city || '');
      setPostalCode(user.postal_code || '');
      setPhone(user.phone || '');
      setEmail(user.email || '');
      
      // Always load photo from backend if available
      if (user.photo) {
        const photoUrl = buildPhotoUrl(user.photo);
        setPhotoPreview(photoUrl);
        setPhotoUrl(photoUrl);
        // Reset photoFile when loading from backend
        setPhotoFile(null);
      } else {
        setPhotoPreview(null);
        setPhotoUrl(null);
        setPhotoFile(null);
      }
    }
  }, [dispatch, userInfo, user, updateSuccess, navigate, BACKEND_URL]);

  // Navigate after successful profile update (but not after photo deletion)
  useEffect(() => {
    if (updateSuccess && !userUpdateProfile.skipNavigation) {
      navigate('/cv-career/cv-generator/section/experience');
    }
  }, [updateSuccess, navigate, userUpdateProfile.skipNavigation]);

  useEffect(() => {
    const img = document.querySelector('.photo-fade-in');
    if (img) {
      img.classList.remove('visible');
    }
  }, [photoPreview]);

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPhotoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const [touched, setTouched] = useState({});

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
  };

  const validate = () => {
    const errors = {};
    if (!name.trim()) errors.name = 'Imię jest wymagane';
    if (!lastName.trim()) errors.lastName = 'Nazwisko jest wymagane';
    if (!birthDate) errors.birthDate = 'Data urodzenia jest wymagana';
    if (!address.trim()) errors.address = 'Adres jest wymagany';
    if (!city.trim()) errors.city = 'Miasto jest wymagane';
    if (!postalCode.trim() || !/^\d{2}-\d{3}$/.test(postalCode)) errors.postalCode = 'Kod pocztowy w formacie 00-000';
    if (!phone.trim() || !/^\d{9,}$/.test(phone.replace(/\D/g, ''))) errors.phone = 'Telefon (min. 9 cyfr)';
    if (!email.trim() || !/^[\w-.]+@([\w-]+\.)+[\w-]{2,}$/.test(email)) errors.email = 'Nieprawidłowy email';
    return errors;
  };

  const errors = validate();
  const isFormValid = Object.keys(errors).length === 0;

  const handleNext = () => {
    if (!isFormValid) return;
    
    const formData = new FormData();
    formData.append('id', user._id);
    formData.append('name', name);
    formData.append('last_name', lastName);
    formData.append('email', email);
    formData.append('birth_date', birthDate);
    formData.append('address', address);
    formData.append('city', city);
    formData.append('postal_code', postalCode);
    formData.append('phone', phone);
    
    if (photoFile) {
      formData.append('photo', photoFile);
    }

    dispatch(updateUserProfile(formData));
    
    // Wait for update to complete before navigating
    // The success flag will trigger navigation via useEffect
  };

  const handleRemovePhoto = () => {
    setPhotoPreview(null);
    setPhotoFile(null);
    setPhotoUrl(null);
    if (photoInputMobileRef.current) photoInputMobileRef.current.value = '';
    if (photoInputDesktopRef.current) photoInputDesktopRef.current.value = '';
    
    // Send request to delete photo from database
    const formData = new FormData();
    formData.append('id', user._id);
    formData.append('clear_photo', 'true');  // Flag to tell backend to delete photo
    formData.append('_skipNavigation', 'true');  // Flag to skip navigation on success
    dispatch(updateUserProfile(formData));
  };

  return (
    <div style={{
      minHeight: '100vh',
      paddingBottom: '4rem',
      overflowX: 'clip',
      width: '100%'
    }}>
      <Container fluid className="px-2 px-md-3">
        <h1 className="mb-4 fw-bold text-center">Dane osobowe</h1>
        <hr />
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-4"
        >
        </motion.div>

        <div className="row g-4">
          {/* Sidebar with progress - Desktop */}
          <div className="col-md-3 d-none d-md-block">
            <CvMultiStepProgress currentStep="Dane osobowe" />
          </div>

          {/* Main Content */}
          <div className="col-12 col-md-9">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <Card className="shadow-lg border-0" style={{ borderRadius: '20px', overflow: 'hidden' }}>
                <Card.Header style={{
                  background: 'linear-gradient(135deg, var(--bs-primary) 0%, #0056b3 100%)',
                  border: 'none',
                  padding: '1.5rem'
                }}>
                  <h5 className="text-white mb-0">
                    Informacje osobiste
                  </h5>
                </Card.Header>
                
                <Card.Body 
                  style={{ padding: '2rem' }} 
                  className="position-relative"
                >
                  <style>{`
                    @media (min-width: 768px) {
                      .form-container-with-photo {
                        padding-right: 160px !important;
                      }
                    }
                  `}</style>

                  {/* Photo Upload - Mobile */}
                  <div className="d-md-none text-center mb-4">
                    <div
                      style={{
                        width: "120px",
                        height: "150px",
                        borderRadius: "15px",
                        background: "#e9ecef",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        overflow: "hidden",
                        boxShadow: "0 0 10px rgba(0,0,0,0.1)",
                        margin: "0 auto",
                        cursor: "pointer",
                        position: "relative"
                      }}
                      onClick={() => photoInputMobileRef.current && photoInputMobileRef.current.click()}
                    >
                      {photoPreview ? (
                        <img
                          key={photoPreview}
                          src={photoPreview}
                          alt="Podgląd zdjęcia"
                          className="photo-fade-in"
                          onLoad={(e) => {
                            e.currentTarget.classList.add('visible');
                          }}
                          onError={() => {
                            // Photo failed to load
                          }}
                          style={{ width: "100%", height: "100%", objectFit: "cover" }}
                        />
                      ) : (
                        <div className="text-center">
                          <FaCamera size={30} className="text-muted mb-2" />
                          <small className="text-muted d-block">Dodaj zdjęcie</small>
                        </div>
                      )}
                    </div>
                    <Form.Control
                      type='file'
                      name='photo'
                      style={{ display: 'none' }}
                      onChange={handlePhotoChange}
                      ref={photoInputMobileRef}
                      accept="image/*"
                    />
                    {photoPreview && (
                      <Button
                        variant="outline-danger"
                        size="sm"
                        className="mt-3"
                        onClick={handleRemovePhoto}
                        style={{ borderRadius: '10px' }}
                      >
                        <FaTrash className="me-2" />
                        Usuń zdjęcie
                      </Button>
                    )}
                  </div>

                  {/* Photo Upload - Desktop (absolute position) */}
                  <div
                    className="d-none d-md-block position-absolute"
                    style={{
                      right: "2rem",
                      top: "2rem",
                      zIndex: 10,
                    }}
                  >
                    <div className="d-flex flex-column align-items-center">
                      <div
                        style={{
                          width: "120px",
                          height: "150px",
                          borderRadius: "15px",
                          background: "#e9ecef",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          overflow: "hidden",
                          boxShadow: "0 0 10px rgba(0,0,0,0.1)",
                          cursor: "pointer",
                          position: "relative"
                        }}
                        onClick={() => photoInputDesktopRef.current && photoInputDesktopRef.current.click()}
                      >
                        {photoPreview ? (
                          <img
                            key={photoPreview}
                            src={photoPreview}
                            alt="Podgląd zdjęcia"
                            className="photo-fade-in"
                            onLoad={(e) => {
                              e.currentTarget.classList.add('visible');
                            }}
                            onError={() => {
                              // Photo failed to load
                            }}
                            style={{ width: "100%", height: "100%", objectFit: "cover" }}
                          />
                        ) : (
                          <div className="text-center">
                            <FaCamera size={30} className="text-muted mb-2" />
                            <small className="text-muted d-block">Dodaj zdjęcie</small>
                          </div>
                        )}
                      </div>
                      <Form.Control
                        type='file'
                        name='photo'
                        style={{ display: 'none' }}
                        onChange={handlePhotoChange}
                        ref={photoInputDesktopRef}
                        accept="image/*"
                      />
                      {photoPreview && (
                        <Button
                          variant="outline-danger"
                          size="sm"
                          className="mt-2"
                          onClick={handleRemovePhoto}
                          style={{ borderRadius: '10px' }}
                        >
                          <FaTrash />
                        </Button>
                      )}
                    </div>
                  </div>

                  <div className="form-container-with-photo">
                    <Form>
                      <div className="row g-3">
                        <div className="col-md-6">
                          <Form.Group>
                            <Form.Label className="fw-medium">Imię</Form.Label>
                            <Form.Control
                              type='text'
                              name='name'
                              value={name}
                              onChange={e => setName(e.target.value)}
                              onBlur={handleBlur}
                              isInvalid={!!errors.name && touched.name}
                              required
                              placeholder="Wpisz imię"
                              style={{ borderRadius: '10px', border: '2px solid #e9ecef' }}
                              className="py-2"
                            />
                            <Form.Control.Feedback type="invalid">{errors.name}</Form.Control.Feedback>
                          </Form.Group>
                        </div>

                        <div className="col-md-6">
                          <Form.Group>
                            <Form.Label className="fw-medium">Nazwisko</Form.Label>
                            <Form.Control
                              type='text'
                              name='lastName'
                              value={lastName}
                              onChange={e => setLastName(e.target.value)}
                              onBlur={handleBlur}
                              isInvalid={!!errors.lastName && touched.lastName}
                              required
                              placeholder="Wpisz nazwisko"
                              style={{ borderRadius: '10px', border: '2px solid #e9ecef' }}
                              className="py-2"
                            />
                            <Form.Control.Feedback type="invalid">{errors.lastName}</Form.Control.Feedback>
                          </Form.Group>
                        </div>

                        <div className="col-md-6">
                          <Form.Group>
                            <Form.Label className="fw-medium">Data urodzenia</Form.Label>
                            <Form.Control
                              type='date'
                              name='birthDate'
                              value={birthDate}
                              onChange={e => setBirthDate(e.target.value)}
                              onBlur={handleBlur}
                              isInvalid={!!errors.birthDate && touched.birthDate}
                              required
                              style={{ borderRadius: '10px', border: '2px solid #e9ecef' }}
                              className="py-2"
                            />
                            <Form.Control.Feedback type="invalid">{errors.birthDate}</Form.Control.Feedback>
                          </Form.Group>
                        </div>

                        <div className="col-md-6">
                          <Form.Group>
                            <Form.Label className="fw-medium">Adres</Form.Label>
                            <Form.Control
                              type='text'
                              name='address'
                              value={address}
                              onChange={e => setAddress(e.target.value)}
                              onBlur={handleBlur}
                              isInvalid={!!errors.address && touched.address}
                              required
                              placeholder="Wpisz adres zamieszkania"
                              style={{ borderRadius: '10px', border: '2px solid #e9ecef' }}
                              className="py-2"
                            />
                            <Form.Control.Feedback type="invalid">{errors.address}</Form.Control.Feedback>
                          </Form.Group>
                        </div>

                        <div className="col-md-6">
                          <Form.Group>
                            <Form.Label className="fw-medium">Miasto</Form.Label>
                            <Form.Control
                              type='text'
                              name='city'
                              value={city}
                              onChange={e => setCity(e.target.value)}
                              onBlur={handleBlur}
                              isInvalid={!!errors.city && touched.city}
                              required
                              placeholder="Wpisz miasto"
                              style={{ borderRadius: '10px', border: '2px solid #e9ecef' }}
                              className="py-2"
                            />
                            <Form.Control.Feedback type="invalid">{errors.city}</Form.Control.Feedback>
                          </Form.Group>
                        </div>

                        <div className="col-md-6">
                          <Form.Group>
                            <Form.Label className="fw-medium">Kod pocztowy</Form.Label>
                            <Form.Control
                              type='text'
                              name='postalCode'
                              value={postalCode}
                              onChange={e => {
                                let val = e.target.value.replace(/\D/g, '');
                                if (val.length > 2) {
                                  val = val.slice(0, 2) + '-' + val.slice(2, 5);
                                }
                                if (val.length > 6) val = val.slice(0, 6);
                                setPostalCode(val);
                              }}
                              onBlur={handleBlur}
                              isInvalid={!!errors.postalCode && touched.postalCode}
                              required
                              placeholder="00-000"
                              maxLength={6}
                              inputMode="numeric"
                              style={{ borderRadius: '10px', border: '2px solid #e9ecef' }}
                              className="py-2"
                            />
                            <Form.Control.Feedback type="invalid">{errors.postalCode}</Form.Control.Feedback>
                          </Form.Group>
                        </div>

                        <div className="col-md-6">
                          <Form.Group>
                            <Form.Label className="fw-medium">Telefon</Form.Label>
                            <Form.Control
                              type='tel'
                              name='phone'
                              value={phone}
                              onChange={e => setPhone(e.target.value)}
                              onBlur={handleBlur}
                              isInvalid={!!errors.phone && touched.phone}
                              required
                              placeholder="np. 123456789"
                              style={{ borderRadius: '10px', border: '2px solid #e9ecef' }}
                              className="py-2"
                            />
                            <Form.Control.Feedback type="invalid">{errors.phone}</Form.Control.Feedback>
                          </Form.Group>
                        </div>

                        <div className="col-md-6">
                          <Form.Group>
                            <Form.Label className="fw-medium">Email</Form.Label>
                            <Form.Control
                              type='email'
                              name='email'
                              value={email}
                              onChange={e => setEmail(e.target.value)}
                              onBlur={handleBlur}
                              isInvalid={!!errors.email && touched.email}
                              required
                              placeholder="np. jan.kowalski@email.com"
                              style={{ borderRadius: '10px', border: '2px solid #e9ecef' }}
                              className="py-2"
                            />
                            <Form.Control.Feedback type="invalid">{errors.email}</Form.Control.Feedback>
                          </Form.Group>
                        </div>
                      </div>
                    </Form>
                  </div>
                </Card.Body>
              </Card>
              {/* Buttons */}
              <div className="row g-3 mt-3">
                <div className="d-flex justify-content-between">
                  <GradientButton
                    onClick={() => navigate('/cv-career')}
                    gradient="pink"
                  >
                    <FaArrowLeft className="me-2" />
                    Wstecz                            
                  </GradientButton>

                  <GradientButton
                    onClick={handleNext}
                    disabled={!isFormValid}
                    gradient="blue"
                  >
                    Dalej
                    <FaArrowRight className="ms-2" />
                  </GradientButton>
                </div>
              </div>
            </motion.div>

            <motion.div className="mt-5">
              <Row className="justify-content-center">
                <Col xs={12}>
                  <div
                    className="p-4 text-center"
                    style={{
                      background: 'rgba(255,255,255,0.05)',
                      borderRadius: '20px',
                      backdropFilter: 'blur(10px)'
                    }}
                  >
                    <h6 className="text-white mb-2">Potrzebujesz pomocy?</h6>
                    <p className="text-white-50 mb-0">
                      <small>
                        Wypełnij wszystkie pola formularza krok po kroku, a system wygeneruje dla Ciebie
                        profesjonalne CV.
                      </small>
                    </p>
                  </div>
                </Col>
              </Row>
            </motion.div>
          </div>
        </div>
      </Container>
    </div>
  );
}

export default CvGeneratorPersonalInfo